import { X, Sparkles, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const FOCUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'margin', label: 'Margin' },
  { value: 'budget', label: 'Budget' },
  { value: 'revenue', label: 'Revenue' },
];

export function DealAdvisorPanel({
  focus,
  onFocusChange,
  result,
  loading,
  error,
  onAnalyze,
  onApplyCta,
  applyingKey,
  onClose,
}) {
  return (
    <div
      className="border border-violet-200 rounded-sm bg-violet-50/90 overflow-hidden flex flex-col max-h-[78vh] shadow-sm"
      data-testid="deal-advisor-panel"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-violet-200 bg-white/80">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-violet-600 shrink-0" />
          <span className="font-semibold text-violet-900 truncate">AI deal advisor</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-violet-700 hover:bg-violet-100"
          aria-label="Close advisor"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-3 py-2 space-y-2 border-b border-violet-100 bg-white/60">
        <p className="text-[11px] uppercase tracking-wide text-violet-800/80 font-semibold">Focus</p>
        <div className="flex flex-wrap gap-1">
          {FOCUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onFocusChange(opt.value)}
              className={cn(
                'rounded px-2 py-1 text-xs font-medium transition-colors',
                focus === opt.value
                  ? 'bg-violet-600 text-white'
                  : 'bg-white text-violet-900 border border-violet-200 hover:bg-violet-100'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2 border-violet-300 text-violet-900 hover:bg-violet-100"
          onClick={onAnalyze}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Run analysis
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm">
        {error && (
          <div className="rounded border border-amber-200 bg-amber-50 px-2 py-2 text-xs text-amber-900">
            {error}
          </div>
        )}

        {result && (
          <>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Summary</p>
              <p className="text-gray-800 leading-snug">{result.summary}</p>
            </div>
            {result.insights?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Insights</p>
                <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                  {result.insights.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.ctas?.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Actions</p>
                <div className="space-y-2">
                  {result.ctas.map((cta, idx) => {
                    const key = `${idx}-${cta.label}`;
                    const busy = applyingKey === key;
                    return (
                      <div
                        key={key}
                        className="rounded border border-white bg-white/90 p-2 shadow-sm space-y-1.5"
                      >
                        <p className="font-medium text-gray-900 text-xs leading-tight">{cta.label}</p>
                        <p className="text-[11px] text-gray-600 leading-snug">{cta.detail}</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-xs h-8 border-violet-300"
                          disabled={loading || busy}
                          onClick={() => onApplyCta(cta, key)}
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {!result && !error && !loading && (
          <p className="text-xs text-violet-900/80">
            Uses a snapshot of this deal (totals and line items). Choose a focus and run analysis. Suggestions are
            validated server-side; Apply runs the same updates as the line table and left panel.
          </p>
        )}
      </div>
    </div>
  );
}
